import React, { useEffect, useMemo, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { formatCommissionPct } from "../../../hooks/useCommissionConfig";
import { bdcApi } from "../../../services/bdcApi";

/**
 * CommissionRates — AD ("Platform Manager") commission-rate management.
 *
 * The backing endpoints (GET/PUT /admin/ad/commission-rates,
 * POST .../overrides, POST .../overrides/:id/end) are being built on the API
 * side in parallel. This page is therefore FEATURE-DETECTED: if the GET 404s
 * (or the response lacks a recognizable rates shape), it renders a
 * "not available yet" card instead of crashing. Every field access is guarded
 * so an unexpected partial shape degrades rather than throws.
 */

// Friendly labels for the commission-rate families we recognize. Unknown keys
// fall back to a humanized form of the key itself.
const FAMILY_LABELS = {
  BC_DIRECT: "BDC direct",
  CONSULTANT_DIRECT: "Consultant direct",
  BC_OVERRIDE: "BDC override",
  DEAL: "Deal",
};

// Default band (fraction units, e.g. 0.0005 = 0.05%) for when the API does not
// supply one, per the plan's [0.05%, 0.5%] band. The API's band arrives as
// fractions too (see data.band), so this fallback stays unit-consistent. The
// rate INPUT is entered in percent units (band * 100).
const DEFAULT_BAND = { min: 0.0005, max: 0.005 };

// Principal-based families an entity override can target (DEAL is fixed-rate).
const FAMILIES = ["BC_DIRECT", "CONSULTANT_DIRECT", "BC_OVERRIDE"];

function familyLabel(key) {
  if (FAMILY_LABELS[key]) return FAMILY_LABELS[key];
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Normalize whatever the GET returns into an array of { family, pct, effective_from, source }.
function normalizeGlobals(raw) {
  if (!raw) return [];
  const src = raw.globals || raw.families || raw.rates || raw;
  if (Array.isArray(src)) {
    return src
      .filter((r) => r && (r.family || r.code))
      .map((r) => ({
        family: r.family || r.code,
        pct: r.pct,
        effective_from: r.effective_from || r.effectiveFrom || null,
        source: r.source || null,
      }));
  }
  if (typeof src === "object") {
    // The committed API nests each family as {global: row|null, overrides, history}
    // (GET /admin/ad/commission-rates); a flat {pct} per family is also accepted.
    return Object.keys(src)
      .filter((k) => {
        const v = src[k];
        if (!v || typeof v !== "object") return false;
        return "pct" in v || (v.global && typeof v.global === "object" && "pct" in v.global);
      })
      .map((k) => {
        const row = "pct" in src[k] ? src[k] : src[k].global;
        return {
          family: k,
          pct: row.pct,
          effective_from: row.effective_from || row.effectiveFrom || null,
          source: row.source || null,
        };
      });
  }
  return [];
}

function CommissionRates() {
  const [loading, setLoading] = useState(true);
  // available: null while loading, false when the endpoint is absent (404) or
  // returns no recognizable shape, true otherwise.
  const [available, setAvailable] = useState(null);
  const [globals, setGlobals] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [history, setHistory] = useState({});
  const [band, setBand] = useState(DEFAULT_BAND);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  // Per-family draft pct + shared change reason.
  const [draft, setDraft] = useState({});
  const [reason, setReason] = useState("");
  const [savingFamily, setSavingFamily] = useState(null);
  const [openHistory, setOpenHistory] = useState(null);

  function applyResponse(res) {
    const data = res?.data ?? res;
    const g = normalizeGlobals(data);
    if (g.length === 0) {
      // A 200 with nothing rate-shaped is treated as "not available" so we
      // never render an empty editor that silently does nothing.
      setAvailable(false);
      return;
    }
    setGlobals(g);
    // Overrides + history arrive nested per family under data.families in the
    // committed API; flatten them (keeping a family key per override row).
    const fams = data.families && typeof data.families === "object" ? data.families : null;
    if (fams) {
      const flatOverrides = Object.keys(fams).reduce((acc, k) => {
        const list = Array.isArray(fams[k].overrides) ? fams[k].overrides : [];
        return acc.concat(list.map((o) => ({ ...o, family: o.rate_family || k })));
      }, []);
      const histObj = Object.keys(fams).reduce((acc, k) => {
        acc[k] = Array.isArray(fams[k].history) ? fams[k].history : [];
        return acc;
      }, {});
      setOverrides(flatOverrides);
      setHistory(histObj);
    } else {
      setOverrides(Array.isArray(data.overrides) ? data.overrides : []);
      setHistory(data.history && typeof data.history === "object" ? data.history : {});
    }
    if (data.band && data.band.min != null && data.band.max != null) {
      setBand({ min: Number(data.band.min), max: Number(data.band.max) });
    }
    const nextDraft = {};
    g.forEach((r) => { nextDraft[r.family] = r.pct != null ? String(normalizePctInput(r.pct)) : ""; });
    setDraft(nextDraft);
    setAvailable(true);
  }

  // Stored pct may be a fraction (0.0025) or already-percent (0.25). Show the
  // input in percent units consistent with the band.
  function normalizePctInput(pct) {
    const n = Number(pct);
    if (Number.isNaN(n)) return "";
    return n > 0 && n < 0.01 ? n * 100 : n;
  }

  function fetchRates() {
    setLoading(true);
    bdcApi
      .adGetCommissionRates()
      .then((res) => {
        applyResponse(res);
        setError(null);
      })
      .catch((err) => {
        const status = err?.response?.status;
        // 404 (route not mounted yet) → graceful "not available".
        if (status === 404 || !err?.response) {
          setAvailable(false);
        } else {
          setAvailable(false);
          setError(err?.response?.data?.message || err.message || "Failed to load commission rates");
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bandLabel = useMemo(
    () => `${formatCommissionPct(band.min)} – ${formatCommissionPct(band.max)}`,
    [band]
  );

  function draftError(family) {
    const raw = draft[family];
    if (raw === "" || raw == null) return null;
    const n = Number(raw);
    if (Number.isNaN(n)) return "Enter a number";
    // Inputs are in PERCENT units; the band arrives as fractions (0.0005-0.005),
    // so compare in percent space (0.05-0.5).
    if (n < band.min * 100 || n > band.max * 100) return `Must be within ${bandLabel}`;
    return null;
  }

  async function saveFamily(family) {
    const raw = draft[family];
    const n = Number(raw);
    if (draftError(family) || raw === "" || raw == null) {
      setFlash({ tone: "danger", text: "Fix the rate value before saving." });
      return;
    }
    if (reason.trim().length < 10) {
      setFlash({ tone: "danger", text: "A change reason (min 10 chars) is required." });
      return;
    }
    setSavingFamily(family);
    setFlash(null);
    try {
      // API contract: field is rate_family and pct is a FRACTION (percent/100).
      await bdcApi.adPutCommissionRates({ rate_family: family, pct: n / 100, reason: reason.trim() });
      setFlash({ tone: "info", text: `${familyLabel(family)} rate updated.` });
      setReason("");
      fetchRates();
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Update failed",
      });
    } finally {
      setSavingFamily(null);
    }
  }

  async function endOverride(id) {
    setFlash(null);
    try {
      await bdcApi.adEndCommissionOverride(id, {});
      setFlash({ tone: "info", text: `Override #${id} ended.` });
      fetchRates();
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Could not end override",
      });
    }
  }

  // Create-override form (2026-07-23 release audit: the page could END
  // overrides but had no way to CREATE one, despite the live API endpoint).
  const [overrideDraft, setOverrideDraft] = useState({
    family: "BC_DIRECT",
    beneficiary: "",
    pct: "",
    reason: "",
  });
  const [creatingOverride, setCreatingOverride] = useState(false);

  async function createOverride(e) {
    e.preventDefault();
    setFlash(null);
    const beneficiaryId = Number(overrideDraft.beneficiary);
    const pctNumber = Number(overrideDraft.pct);
    if (!Number.isInteger(beneficiaryId) || beneficiaryId <= 0) {
      setFlash({ tone: "danger", text: "Enter the beneficiary's user id." });
      return;
    }
    if (!Number.isFinite(pctNumber) || pctNumber <= 0) {
      setFlash({ tone: "danger", text: "Enter the override rate as a percentage, e.g. 0.2." });
      return;
    }
    if (pctNumber < band.min * 100 || pctNumber > band.max * 100) {
      setFlash({
        tone: "danger",
        text: `Rate must stay within the band ${formatCommissionPct(band.min)} – ${formatCommissionPct(band.max)}.`,
      });
      return;
    }
    if (!overrideDraft.reason.trim()) {
      setFlash({ tone: "danger", text: "A reason is required for the audit trail." });
      return;
    }
    setCreatingOverride(true);
    try {
      await bdcApi.adCreateCommissionOverride({
        rate_family: overrideDraft.family,
        beneficiary_user_id: beneficiaryId,
        pct: pctNumber / 100, // API stores the fraction
        reason: overrideDraft.reason.trim(),
      });
      setFlash({ tone: "info", text: `Override created for user #${beneficiaryId}.` });
      setOverrideDraft({ family: "BC_DIRECT", beneficiary: "", pct: "", reason: "" });
      fetchRates();
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Could not create override",
      });
    } finally {
      setCreatingOverride(false);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Commission rates" subtitle={available ? bandLabel : null} />

      {error && <Alert tone="danger">{error}</Alert>}

      {available === false && (
        <section className="card">
          <div className="card__body">
            <h3 style={{ margin: "0 0 8px", fontSize: "var(--t-lg)", fontWeight: 600 }}>
              Commission rate management is not available yet
            </h3>
            <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: 0 }}>
              The commission-rate service is not deployed on this environment. Once the
              API is live this page will let you edit the platform-fee rate families,
              manage beneficiary overrides, and review rate history.
            </p>
          </div>
        </section>
      )}

      {available && (
        <>
          {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

          <section className="card">
            <div className="card__head">
              <div className="card__title">Global rates</div>
            </div>
            <div className="card__body">
              <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
                Rates are expressed in percent and must fall within the platform band{" "}
                <strong>{bandLabel}</strong>. A change reason is required and audited.
              </p>

              <div className="field" style={{ marginBottom: 16 }}>
                <label className="field__label" htmlFor="rate-reason">Change reason (required)</label>
                <textarea
                  id="rate-reason"
                  className="field__textarea"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this rate changing?"
                />
              </div>

              <table className="tbl">
                <thead>
                  <tr>
                    <th>Family</th>
                    <th>Current</th>
                    <th>Effective from</th>
                    <th>New rate (%)</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {globals.map((r) => {
                    const errText = draftError(r.family);
                    return (
                      <tr key={r.family}>
                        <td>{familyLabel(r.family)}</td>
                        <td className="mono">{formatCommissionPct(r.pct) || "—"}</td>
                        <td className="muted">{r.effective_from ? formatAuditTimestamp(r.effective_from) : "—"}</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            min={band.min * 100}
                            max={band.max * 100}
                            className="field__input"
                            style={{ maxWidth: 120 }}
                            value={draft[r.family] ?? ""}
                            onChange={(e) => setDraft({ ...draft, [r.family]: e.target.value })}
                          />
                          {errText && <span className="field__error">{errText}</span>}
                        </td>
                        <td className="r">
                          <Btn
                            variant="primary"
                            size="sm"
                            disabled={savingFamily === r.family || !!errText}
                            onClick={() => saveFamily(r.family)}
                          >
                            {savingFamily === r.family ? "Saving…" : "Save"}
                          </Btn>
                        </td>
                        <td className="r">
                          <Btn
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpenHistory(openHistory === r.family ? null : r.family)}
                          >
                            History
                          </Btn>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {openHistory && (
                <div style={{ marginTop: 12, border: "1px solid var(--bd)", borderRadius: 8, padding: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{familyLabel(openHistory)} history</div>
                  {(Array.isArray(history[openHistory]) ? history[openHistory] : []).length === 0 ? (
                    <Empty>No rate history recorded.</Empty>
                  ) : (
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Rate</th>
                          <th>Effective from</th>
                          <th>Changed by</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history[openHistory].map((h, i) => (
                          <tr key={h.id || i}>
                            <td className="mono">{formatCommissionPct(h.pct) || "—"}</td>
                            <td className="muted">{h.effective_from ? formatAuditTimestamp(h.effective_from) : "—"}</td>
                            {/* The API's audit field is approved_by (user id). */}
                          <td className="muted">{h.approved_by ? `#${h.approved_by}` : "—"}</td>
                            <td>{h.reason || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__title">Beneficiary overrides</div>
            </div>
            <div className="card__body">
              <form onSubmit={createOverride} className="toolbar" style={{ gap: 8, flexWrap: "wrap" }}>
                <select
                  value={overrideDraft.family}
                  onChange={(e) => setOverrideDraft({ ...overrideDraft, family: e.target.value })}
                  aria-label="Override family"
                >
                  {FAMILIES.map((f) => (
                    <option key={f} value={f}>{familyLabel(f)}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Beneficiary user id"
                  value={overrideDraft.beneficiary}
                  onChange={(e) => setOverrideDraft({ ...overrideDraft, beneficiary: e.target.value })}
                  style={{ width: 170 }}
                  aria-label="Beneficiary user id"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Rate %"
                  value={overrideDraft.pct}
                  onChange={(e) => setOverrideDraft({ ...overrideDraft, pct: e.target.value })}
                  style={{ width: 100 }}
                  aria-label="Override rate percent"
                />
                <input
                  type="text"
                  placeholder="Reason (audited)"
                  value={overrideDraft.reason}
                  onChange={(e) => setOverrideDraft({ ...overrideDraft, reason: e.target.value })}
                  style={{ flex: 1, minWidth: 180 }}
                  aria-label="Override reason"
                />
                <Btn type="submit" size="sm" disabled={creatingOverride}>
                  {creatingOverride ? "Creating…" : "Add override"}
                </Btn>
              </form>
            </div>
            <div className="card__body card__body--flush">
              {overrides.length === 0 ? (
                <Empty>No active overrides.</Empty>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Beneficiary</th>
                      <th>Family</th>
                      <th>Rate</th>
                      <th>Effective window</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((o) => (
                      <tr key={o.id}>
                        <td>
                          {/* API fields: beneficiary_user_id (entity override)
                              or consultant_sponsorship_id (relationship). */}
                          <div>
                            {o.beneficiary_user_id
                              ? `User #${o.beneficiary_user_id}`
                              : o.consultant_sponsorship_id
                                ? `Sponsorship #${o.consultant_sponsorship_id}`
                                : "—"}
                          </div>
                        </td>
                        <td><Pill tone="blue">{familyLabel(o.family)}</Pill></td>
                        <td className="mono">{formatCommissionPct(o.pct) || "—"}</td>
                        <td className="muted">
                          {o.effective_from ? formatAuditTimestamp(o.effective_from) : "—"}
                          {" → "}
                          {o.effective_to ? formatAuditTimestamp(o.effective_to) : "open"}
                        </td>
                        <td className="r">
                          {!o.effective_to && (
                            <Btn variant="ghost" size="sm" onClick={() => endOverride(o.id)}>End</Btn>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </>
      )}
    </PageShell>
  );
}

export default CommissionRates;
